<template>
  <template v-if="isShowNode">
    <component
      v-show="languageShow && !hidenEl"
      :id="elUUID"
      :ref="registerRef"
      :is="props.element.name"
      v-bind="componentData"
      :element="props.element"
      :language="props.language"
      :listIndex="props.listIndex"
      :listItemData="props.listItemData"
      :class="className"
      @click="handleClick" />
  </template>
  <template v-else-if="languageShow && !hidenEl">
    <component
      :id="elUUID"
      :ref="registerRef"
      :is="props.element.name"
      v-bind="componentData"
      :element="props.element"
      :language="props.language"
      :listIndex="props.listIndex"
      :listItemData="props.listItemData"
      :class="className"
      @click="handleClick" />
  </template>
</template>

<script setup>
import { ref, toRefs, nextTick, onMounted, provide, computed } from 'vue'
import { initComponentCss } from '@/packages/plugins/hook/useCss'
import { initComponentAnimate, XY_DIALOG_VISIBLE_KEY } from '@/packages/plugins/hook/useAnimate'
import { initLanguagesData } from '@/packages/plugins/hook/useLanguage'
import { initDataSource, saveListClickDataSource } from '@/packages/plugins/hook/useDataSource'
import { emitClickEvents, emitEvents } from '@/packages/plugins/hook/useEvents'
import useComponentRef from '@/packages/plugins/hook/useComponent'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: 'components-template' })
const props = defineProps({
  element: {
    type: Object,
    default: () => ({
      // 组件名称
      name: '',
      // 组件类型
      type: '',
      // 组件id
      uuid: '',
      // 隐藏
      hiden: false,
      // 动画
      animations: [],
      // 组件样式
      style: {},
      // 组件数据
      data: {},
      // 多语言
      languages: [],
      // 数据源
      dataSource: null,
      // 事件
      events: [],
      // 存在失败分支处理的父事件
      failEvents: [],
      // 事件配置
      eventConfig: ''
    })
  },
  language: {
    type: String,
    default: 'en'
  },
  listIndex: {
    type: Object,
    default: null
  },
  listItemData: {
    type: [Object, Array, String, Number, Boolean],
    default: undefined
  }
})
const appStore = useAppStore ? useAppStore() : null
const componentRef = useComponentRef()
const { style, languages, data } = toRefs(props.element)
const { language } = toRefs(props)
const componentData = ref(props.element.data)
/** 业务意图：是否隐藏（事件/数据源只更新此项，由 useAnimate 映射到 hidenEl） */
const hidenTarget = ref(props.element.hiden)
/** 实际是否隐藏，驱动 v-show（含动画过程中的可见性） */
const hidenEl = ref(props.element.hiden)
const isShowNode = ref(false)
// 多语言节点显示
const languageShow = ref(true)
if (props.element.languageShows && props.element.languageShows.length > 0) {
  languageShow.value = props.element.languageShows.includes(language.value)
}

// 生成uuid
const elUUID = ref(props.element.uuid)
if (props.listIndex) {
  const indexs = props.listIndex.index.split(',')
  indexs.forEach((index) => {
    elUUID.value = `${elUUID.value}_${index}`
  })
}
// 样式类名
const className = ref('')
if (props.listIndex && props.element.name !== 'xy-list-item-layout') {
  className.value = 'list-layout ' + props.element.uuid
}

// 生成样式
initComponentCss(props.element.uuid, style, props.element.name, props.listIndex)
// 多语言数据设置
componentData.value = initLanguagesData(languages, language, componentData, data)
// 生成动画
initComponentAnimate(props.element.animations, props.element, hidenTarget, hidenEl)

if (import.meta.env.VITE_APP_TAG === 'H5' && props.element.name === 'xy-dialog') {
  provide(
    XY_DIALOG_VISIBLE_KEY,
    computed(() => !hidenEl.value)
  )
}

// 点击事件
const handleClick = (event) => {
  if (import.meta.env.VITE_APP_TAG === 'H5') {
    // 判断是否为列表节点
    if (props.listIndex) {
      // 是则保存当前点击节点的列表项数据信息
      console.log('---props.listIndex:', props.listIndex)
      saveListClickDataSource(props.listIndex)
    }
    if (props.element?.eventConfig?.debounce) {
      emitClickEvents(props.element.events, props.element.failEvents)
    } else {
      emitEvents(props.element.events, props.element.failEvents)
    }
    // 检查是否为允许透传的特定名称
    const allowPropagationNames = ['xy-svga']
    if (props.element.name && allowPropagationNames.includes(elUUID.value)) {
      return
    }
    event.stopPropagation()
  }
}

// 节点数据动态更新，统一通过cb控制
const changeNodeDataCb = (key, value) => {
  // 节点显示隐藏
  if (key === 'hiden') {
    hidenTarget.value = value
  }
}

if (import.meta.env.VITE_APP_TAG === 'H5') {
  const nodesMapKeys = Object.keys(appStore.nodesMap)
  if (nodesMapKeys.includes(props.element.uuid)) {
    appStore.nodesMap[props.element.uuid] = changeNodeDataCb
  }
  // 设置节点模式
  if (appStore.hiddenNodes.includes(props.element.uuid) || props.element.name === 'xy-dialog' || props.element.name === 'xy-svga') {
    isShowNode.value = true
  }
}

// 挂载节点
const registerRef = (el) => {
  componentRef.setComponentRef(props.element.name, elUUID.value, el)
}

onMounted(() => {
  nextTick(() => {
    // 数据源设置
    initDataSource(elUUID.value, props.element.dataSource, componentData, hidenTarget, props.listIndex, props.listItemData)
  })
})

// if (import.meta.env.VITE_APP_TAG === 'H5') {
//   watch(
//     () => props.listItemData,
//     () => {
//       if (props.listItemData === undefined || !props.listIndex) return
//       applyListItemDataSourceFills(props.element.dataSource, componentData, hidenTarget, props.listIndex, props.listItemData)
//     },
//     { deep: true }
//   )
// }
</script>
